import type {
  CreateScheduledPostDto,
  MediaAssetDto,
  Platform as ApiPlatform,
} from '@reelplicate/api-client/model';
import { fromWallClock, wallClock } from '@reelplicate/flows';

import { zoneOffset } from '@/features/settings/timezones';

/** TikTok's and Instagram's caption limit. */
export const CAPTION_MAX = 2200;
/** Platforms with their own title field, and how long a title they take. */
export const TITLE_MAX: Partial<Record<ApiPlatform, number>> = {
  TIKTOK: 90,
  YOUTUBE: 100,
};
export type TitledPlatform = 'TIKTOK' | 'YOUTUBE';
export const hasTitle = (platform: ApiPlatform): platform is TitledPlatform =>
  TITLE_MAX[platform] !== undefined;
/** The longest title every picked platform takes (TikTok's when it's picked). */
export const titleMax = (platforms: ApiPlatform[]) =>
  Math.min(...platforms.flatMap((p) => TITLE_MAX[p] ?? []));

/** Carousels: Instagram's limit. */
export const MAX_SLIDES = 10;
/** Reels: TikTok's upload limit for scheduled posts. */
export const MAX_REEL_SECONDS = 180;

export type PostType = 'reel' | 'carousel';

/** A library asset on the post, with how it got there. */
export type PickedMedia = MediaAssetDto & { via: 'media' | 'upload' };

export type PostForm = {
  accountIds: string[];
  dataType: PostType;
  media: PickedMedia[];
  /** The post's name in Reelplicate; not posted. */
  title: string;
  caption: string;
  /** The title for TikTok's and YouTube's own title field. */
  postTitle: string;
  /** Captions (and titles) per platform instead of one for all. */
  perPlatform: boolean;
  captions: Partial<Record<ApiPlatform, string>>;
  postTitles: Partial<Record<TitledPlatform, string>>;
  when: 'now' | 'schedule';
  /** The day on the workspace's calendar (local midnight, for the date field). */
  date: Date | undefined;
  /** "13:00" on the workspace's clock. */
  time: string | undefined;
};

const pad = (n: number) => String(n).padStart(2, '0');

/** The calendar day and clock time at an instant, in `timeZone`. */
export function toWall(at: Date, timeZone: string): { date: Date; time: string } {
  const w = wallClock(at, timeZone);
  return {
    date: new Date(w.year, w.month - 1, w.day),
    time: `${pad(w.hour)}:${pad(w.minute)}`,
  };
}

/** The instant a day and time on `timeZone`'s clock mean. */
export function toInstant(date: Date, time: string, timeZone: string): Date {
  const [hour, minute] = time.split(':').map(Number) as [number, number];
  return fromWallClock(
    {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      hour,
      minute,
    },
    timeZone,
  );
}

/**
 * The zone a post is scheduled in: its persona's, when every picked account
 * posts as one persona that has one; otherwise the workspace's.
 */
export function postZone(
  accountIds: string[],
  accounts: { id: string; personaId?: string | null }[],
  groups: { id: string; name: string; timezone?: string | null }[],
  workspaceZone: string,
): { timeZone: string; persona?: string } {
  const personaIds = new Set(
    accounts.filter((a) => accountIds.includes(a.id)).map((a) => a.personaId ?? null),
  );
  const [only] = personaIds;
  const group =
    personaIds.size === 1 && only ? groups.find((g) => g.id === only) : undefined;
  return group?.timezone
    ? { timeZone: group.timezone, persona: group.name }
    : { timeZone: workspaceZone };
}

/** "2026-09-25" for a date field's day. */
export const isoDay = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** "Europe/London (GMT+1)" */
export const zoneLabel = (timeZone: string, at: Date) =>
  `${timeZone.replaceAll('_', ' ')} (${zoneOffset(timeZone, at)})`;

/** Characters as people count them (an emoji is one). */
export const length = (text: string) => [...text].length;

/** What's missing before the post can be scheduled, per field. */
export function problems(
  form: PostForm,
  timeZone: string,
  now: Date,
  platforms: ApiPlatform[] = [],
): Partial<
  Record<'accounts' | 'media' | 'title' | 'when' | 'caption' | 'postTitle', string>
> {
  const out: ReturnType<typeof problems> = {};
  if (form.accountIds.length === 0) out.accounts = 'Choose at least one account.';
  if (form.media.length === 0) {
    out.media = form.dataType === 'reel' ? 'Add a reel.' : 'Add at least one image.';
  }
  if (!form.title.trim()) out.title = 'Give the post a name.';
  const titled = platforms.filter(hasTitle);
  const tooLong = form.perPlatform
    ? titled.find((p) => length(form.postTitles[p] ?? form.postTitle) > TITLE_MAX[p]!)
    : titled.find((p) => length(form.postTitle) > TITLE_MAX[p]!);
  if (tooLong) {
    out.postTitle = `${tooLong === 'TIKTOK' ? 'TikTok' : 'YouTube'} titles are ${TITLE_MAX[tooLong]} characters at most.`;
  }
  const captions = form.perPlatform ? Object.values(form.captions) : [form.caption];
  if (captions.some((c) => c && length(c) > CAPTION_MAX)) {
    out.caption = `Keep captions to ${CAPTION_MAX.toLocaleString('en-US')} characters.`;
  }
  if (form.when === 'schedule') {
    if (!form.date || !form.time) out.when = 'Pick a day and time.';
    else if (toInstant(form.date, form.time, timeZone) <= now) {
      out.when = 'Pick a time in the future.';
    }
  }
  return out;
}

/** The API body for the form. Drafts keep a time even when "Post now" is picked. */
export function toBody(
  form: PostForm,
  { draft, timeZone, now }: { draft: boolean; timeZone: string; now: Date },
): CreateScheduledPostDto {
  const at = form.date && form.time ? toInstant(form.date, form.time, timeZone) : now;
  const postNow = !draft && form.when === 'now';
  return {
    accountIds: form.accountIds,
    dataType: form.dataType,
    mediaAssetIds: form.media.map((m) => m.id),
    title: form.title.trim(),
    caption: form.caption,
    postTitle: form.postTitle,
    ...(form.perPlatform ? { captions: form.captions, postTitles: form.postTitles } : {}),
    ...(postNow ? { postNow: true } : { scheduledAt: at.toISOString() }),
    draft,
  };
}

/** Whether a library asset can go in a post of this type. */
export function fits(asset: MediaAssetDto, type: PostType): boolean {
  const kind = asset.contentType?.split('/')[0];
  if (type === 'carousel') return kind === 'image';
  if (kind !== 'video') return false;
  const vertical = !asset.width || !asset.height || asset.height > asset.width;
  return vertical && (asset.durationSeconds ?? 0) <= MAX_REEL_SECONDS;
}
